import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

import type { ModelFormValues } from '../api/model';
import { TimeSearchResult } from '../api/tag';
import { AnomalySort, HiddenTag } from '../tag';
import { TimePresetKeyType, TimeRangesType } from '../time-selection';

import type { AlertBoardEntry } from './alertV2';
import { ParentTag } from './folder';

import { AssessMode, Ensemble, Tag, TimeSelection } from '@controlrooms/models';
import { ChartMode } from '@controlrooms/web/src/app-v2/context/investigate-chart-context';

export enum ViewType {
  MONITOR = 'monitor',
  ANALYZE = 'analyze',
  INVESTIGATE = 'investigate',
  ALERT_BOARD = 'alert_board',
  ALERT_MANAGER = 'alert_manager',
  LIMIT_MANAGER = 'limit_manager',
  MANAGE_MONITOR = 'manage_monitor',
  MANAGE_FORMULAS = 'manage_formulas',
  MANAGE_ALERTS = 'manage_alerts',
  MANAGE_INHERITANCE = 'manage_inheritance',
  MANAGE_USER_GROUPS = 'manage_user_groups',
  MANAGE_WEBHOOKS = 'manage_webhooks',
}

export interface ViewCollectionUpdate {
  collection_name: string;
  add_views: string[];
  remove_views: string[];
}

export interface PersistView {
  user_id?: number;
  view_id?: string;
  version?: number;
  view: View;
  shared?: boolean;
  ensemble_family_id?: string;
  view_name?: string;
  default_view?: boolean;
  share_type?: ViewShareType;
  description?: string;
  created_by?: string;
  view_history?: PersistView[];
  view_type?: 'ALERT_BOARD' | 'VIEWS' | 'ALERT_MANAGER' | 'LIMIT_MANAGER' | 'MANAGE';
}
export interface PersistCollection {
  collection_id?: string;
  collection_name: string;
  description?: string;
  view_ids: string[];
  views?: PersistView[];
  shareType?: ViewShareType;
}
export interface PatchCollection {
  collection_name: string;
  description: string;
  add_views: string[];
  remove_views: string[];
}

export enum ViewShareType {
  PRIVATE = 'Private',
  VIEWABLE = 'Viewable',
  EDITABLE = 'Editable',
  ANONYMOUS = 'Anonymous',
}

export interface ViewState {
  viewId: string;
  version: number | undefined;
  userId: number | undefined;
  isDefault: boolean;
  selectedMode: ViewType;
  name: string;
  description: string;
  created_by?: string;
  isHidden?: boolean;
  isDirty?: boolean;
  isDeleted?: boolean;
  tenant_id: number;
  view: TabbedView;
  dataModeType?: DataModeType;
  timeSelection: TimeSelection;
  timeSelectionHistory: TimeSelection[];
  view_id: string;
  config: ViewConfig;
  selectedEnsemble: Ensemble | null;
  shareType?: ViewShareType;
  shared: boolean;
  tabType?: 'ALERT_BOARD' | 'VIEWS' | 'ALERT_MANAGER' | 'LIMIT_MANAGER' | 'MANAGE';
  subPage?: 'monitors' | 'formulas' | 'inheritance' | 'alerts' | 'user_groups' | 'webhooks';
  // isNewDefault?: boolean;
}

export interface ViewConfig {
  [ViewType.ANALYZE]: {
    chartHeight: number;
    limitFilters: {
      HH: boolean;
      H: boolean;
      L: boolean;
      LL: boolean;
    };
    sortOption: AnomalySort;
    assessMode?: AssessMode;
    modelFilterTypes?: string[];
    showOnlyAlertedModels?: boolean;
    showMAEBand?: boolean;
    selectedEnsembleId?: number | null;
    browserState?: {
      expandedSystems: number[];
    };
  };
}

export class PersistTimeSelection {
  startTime: string;
  endTime: string;
  timezone: string;
  streamingTimeInSeconds: number | undefined;
  timeRange?: TimeRangesType;
  timePreset?: TimePresetKeyType;

  constructor(
    startTime: string,
    endTime: string,
    timezone: string,
    streamingTimeInSeconds: number | undefined,
    timeRange: TimeRangesType | undefined,
    timePreset: TimePresetKeyType | undefined,
  ) {
    this.startTime = startTime;
    this.endTime = endTime;
    this.timezone = timezone;
    this.streamingTimeInSeconds = streamingTimeInSeconds;
    this.timeRange = timeRange;
    this.timePreset = timePreset;
  }

  public static ofTimeSelection(init: TimeSelection) {
    return new PersistTimeSelection(
      init.startTime.toISOString(),
      init.endTime.toISOString(),
      init.timezone,
      init.streamingTimeInSeconds,
      init.timeRange,
      init.timePreset,
    );
  }

  public static toTimeSelection(init: PersistTimeSelection) {
    return new TimeSelection(
      dayjs.utc(init.startTime),
      dayjs.utc(init.endTime),
      init.timezone,
      init.streamingTimeInSeconds,
      init.timeRange,
      init.timePreset,
    );
  }
}

export enum DataModeType {
  AUTO = 'AUTO',
  RAW = 'RAW',
  RESAMPLED = 'RESAMPLED',
}

export interface View {
  sub_page?: 'monitors' | 'formulas' | 'inheritance' | 'alerts' | 'user_groups' | 'webhooks';
  pinnedTags?: ParentTag[];
  hiddenTags?: HiddenTag[];
  type: ViewType;
  timeSelection: PersistTimeSelection;
  selectedFolders: number[];
  showLimits?: boolean;
  dataModeType?: DataModeType;
  ensemble_family_id?: string;
  showAnalyzeLimits?: boolean;
  showLabeledEvents?: boolean;
  showMonitorLimits?: boolean;
  showInvestigateLimits?: boolean;
  severityFilter?: number;
  selectedTags?: string[] | { [folder: number]: Tag[] };
  showAnomalies?: boolean;
  chartMode?: ChartMode;
  groupByUom?: boolean;
  name: string;
  description?: string;
  isTrendSearch?: boolean;
  trendSearchData?: TrendSearchData;
  config?: ViewConfig;
}

export interface MonitorView extends View {
  type: ViewType.MONITOR;
  severityFilter?: number;
  showMonitorLimits?: boolean;
}

export interface SelectedModelPreviewKey {
  processId: number;
  modelId: number;
}

export interface AnalyzeView extends View {
  type: ViewType.ANALYZE;
  pinnedTags: ParentTag[];
  hiddenTags?: HiddenTag[];
  showAnalyzeLimits?: boolean;
  selectedModelPreview?: SelectedModelPreviewKey | null;
}
export interface InvestigateView extends View {
  type: ViewType.INVESTIGATE;
  selectedTags: string[];
}

interface SavedView {
  name: string;
  id?: string;
}

export interface DeleteView {
  view_id: string;
  user_id: number;
}

/** Event search (trend search) knob parameters – persisted per tab inside trendSearchData */
export interface EventParameter {
  duration: number;
  shape: number;
  level: number;
  amplitude: number;
}

export interface TrendSearchData {
  id?: number;
  startTime: string;
  endTime: string;
  folder: number;
  tag: Tag;
  tagName: string;
  timeRange: string;
  parameters?: {
    duration: number;
    shape: number;
    level: number;
    amplitude: number;
  };
  /** Event search parameters (duration, shape, level, amplitude) – persisted with trend search */
  eventParameter?: EventParameter;
  /** Selected search event (for default tab / form); cleared when leaving trend search */
  selectedEvent?: {
    id?: number;
    startTime: string;
    endTime: string;
    folder: number;
    tag: Tag;
    tagName: string;
  };
  selectedEventTab?: TimeSearchResult;
  selectedEvents?: TimeSearchResult[];
}

export type InitialStateType = Record<string, boolean>;

export interface MonitorModeView {
  selectedFolders: number[];
  ensemble_family_id?: string;
  selectedTags: string[];
  severityFilter?: number;
  showLimits?: boolean;
  dataModeType?: DataModeType;
  showLabeledEvents?: boolean;
  showMonitorLimits?: boolean;
  selectedLabelTypes?: InitialStateType;
}

export interface AnalyzeModeView {
  selectedFolders: number[];
  ensemble_family_id?: string;
  selectedTags: string[];
  severityFilter?: number;
  showLimits?: boolean;
  showLabeledEvents?: boolean;
  showAnalyzeLimits?: boolean;
  dataModeType?: DataModeType;
  pinnedTags?: ParentTag[];
  hiddenTags?: HiddenTag[];
  trendSearchData?: TrendSearchData;
  isTrendSearch?: boolean;
  isTrendSearchEnabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface InvestigateModeView {
  ensemble_family_id?: string;
  selectedFolders: number[];
  selectedTags: { [folder: number]: Tag[] };
  showLimits?: boolean;
  showLabeledEvents?: boolean;
  showInvestigateLimits?: boolean;
  showAnomalies?: boolean;
  dataModeType?: DataModeType;
  organize?: ChartMode;
  severityFilter?: number;
  groupByUom: boolean;
  /** Tag highlighted on chart (hover) */
  highlightedTag?: string | null;
  /** Tag locked on chart */
  lockedTag?: string | null;
}

/** Preview pane state (time range, history) for the selected alert */
export interface AlertBoardPreviewState {
  timeSelection?: TimeSelection | null;
  timeSelectionHistory?: TimeSelection[];
}

// View types for different tab types
export interface AlertBoardView {
  selected_board?: string;
  selected_view?: string;
  display_mode?: string;
  time_selection?: PersistTimeSelection;
  filters?: Record<string, unknown>;
  table_config?: Record<string, unknown>;
  selectedAlert?: AlertBoardEntry | null;
  preview?: AlertBoardPreviewState;
}

export interface AlertManagerView {
  folder?: number;
  tag?: string;
  rule_id?: string;
}

export interface LimitManagerView {
  folder?: number;
  tag?: string;
  limit_id?: string;
}

/**
 * Breadcrumb path structure for nested tab navigation.
 * Represents the full navigation hierarchy as an array of strings.
 * Acts like a breadcrumb - clicking any segment navigates back to that level.
 *
 * Examples:
 * - [] or undefined: Root/list page (no tabs)
 * - ['details']: Details tab (breadcrumb: Details)
 * - ['details', 'groups']: Groups sub-tab (breadcrumb: Details > Groups)
 * - ['details', 'groups', 'edit']: Edit sub-tab (breadcrumb: Details > Groups > Edit)
 * - ['alerts', 'rules']: Rules sub-tab (breadcrumb: Alerts > Rules)
 *
 * This flexible structure supports any depth of nested tabs.
 * Each segment represents a level in the navigation hierarchy.
 */
export type TabPath = string[];

/** Model list table filter state (persisted per tab) */
export interface ManageMonitorModelDataFilters {
  dataType: string[];
  modelType: string;
  modelStatus: string;
}

// Manage view types based on subPage
export interface ManageMonitorView {
  folder?: number;
  tag?: string;
  ensemble_id?: string;
  /**
   * Nested tab path for flexible tab navigation.
   * Supports any depth of nested tabs.
   */
  tabPath?: TabPath;
  sub_page?: 'monitors' | 'formulas' | 'inheritance' | 'alerts' | 'user_groups' | 'webhooks';
  /** Table filters for the model list (data type, model type, status) */
  modelDataFilters?: ManageMonitorModelDataFilters;
  /** Persisted form data for model details view */
  model_config?: Partial<ModelFormValues>;
  /** ensemble_id of model shown in preview panel */
  selectedModelPreviewEnsembleId?: number | null;
  /** Selected row IDs in the model table (persisted per tab) */
  selectedRowIds?: (string | number)[];
}

export interface ManageFormulasView {
  folder?: number;
  tag?: string;
  ensemble_id?: string;
}

export interface ManageInheritanceView {
  folder?: number;
  tag?: string;
  ensemble_id?: string;
}

export interface ManageAlertsView {
  folder?: number;
  tag?: string;
}

export interface ManageUserGroupsView {
  user_group_id?: number;
}

export interface ManageWebhooksView {
  webhook_id?: string;
}

export type TabbedView = {
  // VIEWS tab types - direct indexing for backward compatibility
  [ViewType.MONITOR]: MonitorModeView;
  [ViewType.ANALYZE]: AnalyzeModeView;
  [ViewType.INVESTIGATE]: InvestigateModeView;
  // Other tab types
  [ViewType.ALERT_BOARD]: AlertBoardView;
  [ViewType.ALERT_MANAGER]: AlertManagerView;
  [ViewType.LIMIT_MANAGER]: LimitManagerView;
  // MANAGE sub-page types
  [ViewType.MANAGE_MONITOR]: ManageMonitorView;
  [ViewType.MANAGE_FORMULAS]: ManageFormulasView;
  [ViewType.MANAGE_INHERITANCE]: ManageInheritanceView;
  [ViewType.MANAGE_ALERTS]: ManageAlertsView;
  [ViewType.MANAGE_USER_GROUPS]: ManageUserGroupsView;
  [ViewType.MANAGE_WEBHOOKS]: ManageWebhooksView;
};

export interface MonitorSavedView extends MonitorView, SavedView {}
export interface AnalyzeSavedView extends AnalyzeView, SavedView {}
export interface InvestigateSavedView extends InvestigateView, SavedView {}
